import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarCheck,
  Star,
  TrendingUp,
  ChevronRight,
  MapPin,
  Sparkles,
  Clock,
  Activity,
  Zap,
  Target,
  PencilLine,
  Check,
  Bell,
  Store,
  ClipboardList,
  CalendarClock,
  BarChart3,
  LifeBuoy,
  HeartPulse,
  Flame,
  CalendarRange,
  PiggyBank,
  Percent,
} from 'lucide-react';
import StatCard from '../components/StatCard';
import GaugeChart from '../components/Gauge';
import { occupancyColor, occupancySolid } from '../lib/heatColor';
import { getMyGym } from '../api/gymApi';
import { getMyGymBookings, getMyGymBookingCalendar } from '../api/bookingApi';
import { getMyGymAnalytics } from '../api/analyticsApi';
import { getNotifications } from '../api/notificationApi';
import { getMyGymReviews } from '../api/reviewApi';
import { computeEffectiveSlots, bookingCountsForDate, withRemainingCapacity } from '../components/calendar/useEffectiveSlots';
import { toDateKey, weekdayKeyOf, addDays } from '../components/calendar/dateUtils';

const TIME_BANDS = [
  { label: 'Morning', startMin: 6 * 60, endMin: 10 * 60 },
  { label: 'Midday', startMin: 10 * 60, endMin: 14 * 60 },
  { label: 'Afternoon', startMin: 14 * 60, endMin: 18 * 60 },
  { label: 'Evening', startMin: 18 * 60, endMin: 22 * 60 },
];

function timeToMin(hhmm) {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

// Attaches startMin/endMin/booked to each of a date's effective slots, from the
// weeklySlots/slotOverrides template plus that date's booking counts.
function decoratedSlotsForDate(gym, calendarBookings, date) {
  const dateKey = toDateKey(date);
  const effective = computeEffectiveSlots(gym.weeklySlots, gym.slotOverrides, dateKey, weekdayKeyOf(date));
  const counts = bookingCountsForDate(calendarBookings, dateKey);
  const withBooked = withRemainingCapacity(effective.slots, counts).map((s) => ({
    ...s,
    startMin: timeToMin(s.startTime),
    endMin: timeToMin(s.endTime),
    booked: Math.max(0, (Number(s.capacity) || 0) - (s.remainingCapacity ?? s.capacity)),
    start: s.startTime,
    end: s.endTime,
  }));
  return { ...effective, slots: withBooked.filter((s) => s.isActive !== false).sort((a, b) => a.startMin - b.startMin) };
}

function bandsForSlots(slots) {
  return TIME_BANDS.map((band) => {
    const bandSlots = slots.filter((s) => s.startMin >= band.startMin && s.startMin < band.endMin);
    const capacity = bandSlots.reduce((a, s) => a + s.capacity, 0);
    const booked = bandSlots.reduce((a, s) => a + s.booked, 0);
    return { booked, capacity, pct: capacity > 0 ? booked / capacity : null };
  });
}

function usePersisted(key, initial) {
  const [value, setValue] = useState(() => {
    try {
      const saved = localStorage.getItem(key);
      return saved ? JSON.parse(saved) : initial;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);
  return [value, setValue];
}

// Real gym-api fields (Gym.js model), not the concept's mock shape.
function computeProfileScore(gym) {
  const checks = [
    !!gym.name,
    !!gym.tagline,
    !!gym.phone && !!gym.email,
    !!gym.description,
    !!gym.coverImage,
    (gym.amenities || []).length > 0,
    (gym.equipment || []).length > 0,
    (gym.yearsInBusiness || 0) > 0,
  ];
  const done = checks.filter(Boolean).length;
  return Math.round((done / checks.length) * 100);
}

function trendFor(current, previous) {
  if (previous === 0) return current > 0 ? { direction: 'up', label: 'new' } : null;
  const pct = Math.round(((current - previous) / previous) * 100);
  if (pct === 0) return { direction: 'flat', label: 'steady' };
  return { direction: pct > 0 ? 'up' : 'down', label: `${Math.abs(pct)}% vs last wk` };
}

// Horizontal "today at a glance" timeline: one segment per slot, colored by occupancy,
// with a live marker for the current time.
function ScheduleTimeline({ slots, dayCapacity, dayBooked, dayOccupancyPct, nowMin, closed, nextSlot }) {
  if (closed) {
    return <p className="text-sm text-[#9aa0a6] py-10 text-center">Closed today.</p>;
  }
  if (slots.length === 0) {
    return <p className="text-sm text-[#9aa0a6] py-10 text-center">No slots configured for today.</p>;
  }

  const dayStart = slots[0].startMin;
  const dayEnd = slots[slots.length - 1].endMin;
  const span = dayEnd - dayStart || 1;
  const nowVisible = nowMin >= dayStart && nowMin <= dayEnd;
  const nowPct = Math.min(100, Math.max(0, ((nowMin - dayStart) / span) * 100));

  return (
    <div>
      <div className="relative mt-7">
        {nowVisible && (
          <div className="absolute -top-6 -translate-x-1/2" style={{ left: `${nowPct}%` }}>
            <span className="text-[10px] font-bold text-[#1a73e8] dark:text-[#8ab4f8] whitespace-nowrap px-1.5 py-0.5 rounded-md bg-[#e8f0fe] dark:bg-[#1a2e4c]">Now</span>
          </div>
        )}
        <div className="flex h-12 rounded-xl overflow-hidden gap-[2px]">
          {slots.map((s, i) => {
            const pct = s.capacity > 0 ? s.booked / s.capacity : 0;
            const isPast = s.endMin <= nowMin;
            const widthPct = ((s.endMin - s.startMin) / span) * 100;
            return (
              <div
                key={i}
                className="h-full flex items-center justify-center text-[10px] font-bold text-white transition-opacity"
                style={{ width: `${widthPct}%`, backgroundColor: occupancySolid(pct), opacity: isPast ? 0.35 : 1 }}
                title={`${s.start}–${s.end}: ${s.booked}/${s.capacity} booked`}
              >
                {widthPct > 6 ? `${s.booked}/${s.capacity}` : ''}
              </div>
            );
          })}
        </div>
        {nowVisible && <div className="absolute top-0 bottom-0 w-0.5 bg-[#202124] dark:bg-white rounded-full" style={{ left: `${nowPct}%` }} />}
      </div>
      <div className="flex items-center justify-between text-[11px] text-[#9aa0a6] mt-1.5">
        <span>{slots[0].start}</span>
        <span>{slots[slots.length - 1].end}</span>
      </div>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-5 pt-4 border-t border-[#e8eaed] dark:border-[#3c4043] text-xs">
        <span className="text-[#5f6368] dark:text-[#9aa0a6]">
          Occupancy today: <strong className="text-[#202124] dark:text-[#e8eaed]">{dayBooked}/{dayCapacity} ({dayOccupancyPct}%)</strong>
        </span>
        {nextSlot && (
          <span className="flex items-center gap-1 text-[#1a73e8] dark:text-[#8ab4f8] font-medium">
            <Zap className="w-3.5 h-3.5" /> Next: {nextSlot.start} ({nextSlot.booked}/{nextSlot.capacity} booked)
          </span>
        )}
      </div>
    </div>
  );
}

const QUICK_ACTIONS = [
  { id: 'listing', label: 'Edit Listing', icon: Store, path: '/my-gym', tone: 'blue' },
  { id: 'bookings', label: 'View Bookings', icon: ClipboardList, path: '/bookings', tone: 'green' },
  { id: 'slots', label: 'Manage Slots', icon: CalendarClock, path: '/pricing-slots', tone: 'purple' },
  { id: 'analytics', label: 'Analytics', icon: BarChart3, path: '/analytics', tone: 'amber' },
  { id: 'support', label: 'Get Support', icon: LifeBuoy, path: '/support', tone: 'red' },
];

const TONE_CLASSES = {
  blue: 'bg-blue-50 dark:bg-blue-950/40 text-[#1a73e8]',
  green: 'bg-green-50 dark:bg-green-950/40 text-[#34A853]',
  purple: 'bg-purple-50 dark:bg-purple-950/40 text-[#9334e6]',
  amber: 'bg-amber-50 dark:bg-amber-950/40 text-[#f29900]',
  red: 'bg-red-50 dark:bg-red-950/40 text-[#EA4335]',
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const [now] = useState(() => new Date());
  const [loading, setLoading] = useState(true);
  const [gym, setGym] = useState(null);
  const [noGym, setNoGym] = useState(false);
  const [recentBookings, setRecentBookings] = useState([]);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [dailyTrend, setDailyTrend] = useState([]);
  const [monthTrend, setMonthTrend] = useState([]);
  const [todaySlots, setTodaySlots] = useState([]);
  const [todayConfig, setTodayConfig] = useState({ closed: true });
  const [twoDayHeat, setTwoDayHeat] = useState([]);
  const [twoDayActiveCount, setTwoDayActiveCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [reviews, setReviews] = useState([]);

  const [monthlyGoal, setMonthlyGoal] = usePersisted('gym_portal_revenue_goal', 60000);
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalDraft, setGoalDraft] = useState(String(monthlyGoal));

  const todayStr = toDateKey(now);
  const tomorrowDate = addDays(now, 1);
  const tomorrowStr = toDateKey(tomorrowDate);
  const nowMin = now.getHours() * 60 + now.getMinutes();

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const [gymData, calendar, bookingsData, upcomingData, trend30, trendAll, notifData, reviewsData] = await Promise.all([
          getMyGym(),
          getMyGymBookingCalendar(todayStr, tomorrowStr),
          getMyGymBookings({ limit: 8 }),
          getMyGymBookings({ tab: 'upcoming', limit: 5 }),
          getMyGymAnalytics({ range: '30' }),
          getMyGymAnalytics({ range: 'all' }),
          getNotifications({ limit: 10 }),
          getMyGymReviews({ limit: 5 }),
        ]);
        if (cancelled) return;

        setGym(gymData);
        setRecentBookings(bookingsData?.bookings || []);
        setUpcomingBookings(upcomingData?.bookings || []);
        setDailyTrend(trend30?.trend || []);
        setMonthTrend(trendAll?.trend || []);
        setNotifications(Array.isArray(notifData?.notifications) ? notifData.notifications : []);
        setReviews(Array.isArray(reviewsData?.reviews) ? reviewsData.reviews : []);

        const calendarBookings = calendar?.bookings || [];
        const todayDecorated = decoratedSlotsForDate(gymData, calendarBookings, now);
        const tomorrowDecorated = decoratedSlotsForDate(gymData, calendarBookings, tomorrowDate);
        setTodaySlots(todayDecorated.slots);
        setTodayConfig({ closed: todayDecorated.isClosed });
        setTwoDayHeat([
          { label: 'Today', closed: todayDecorated.isClosed, bands: bandsForSlots(todayDecorated.slots) },
          { label: 'Tomorrow', closed: tomorrowDecorated.isClosed, bands: bandsForSlots(tomorrowDecorated.slots) },
        ]);
        setTwoDayActiveCount(
          calendarBookings.filter((b) => (b.date === todayStr || b.date === tomorrowStr) && b.status !== 'cancelled').length
        );
      } catch (err) {
        setGym(null);
        if (err.response?.status === 404) setNoGym(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-40">
        <div className="w-10 h-10 rounded-full border-4 border-[#e8f0fe] dark:border-[#1a2e4c] border-t-[#1a73e8] animate-spin" />
      </div>
    );
  }

  if (noGym || !gym) {
    return (
      <div className="google-card p-10 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043] flex flex-col items-center text-center gap-3">
        <div className="p-3 rounded-2xl bg-blue-50 dark:bg-blue-950/40 text-[#1a73e8]">
          <Store className="w-6 h-6" />
        </div>
        <h2 className="text-lg font-bold text-[#202124] dark:text-[#e8eaed]">Create your gym listing to get started</h2>
        <p className="text-sm text-[#5f6368] dark:text-[#9aa0a6] max-w-sm">
          Your dashboard fills up with bookings, revenue, and occupancy once you&apos;ve registered a gym.
        </p>
        <button
          onClick={() => navigate('/my-gym')}
          className="mt-2 px-4 py-2 rounded-xl text-sm font-semibold bg-[#1a73e8] text-white hover:bg-[#1765cc] transition-colors cursor-pointer flex items-center gap-1.5"
        >
          <Store className="w-4 h-4" /> Create Gym Listing
        </button>
      </div>
    );
  }

  const dailyPrice = gym.dailyPassPrice || 0;

  // Revenue is derived as bookingCount × dailyPassPrice — the flat-rate estimate
  // getMyGymAnalytics itself uses today, since Booking has no per-booking amount
  // or passType yet (planned: gym-api schema addition + analytics extension).
  const last7 = dailyTrend.filter((t) => t.label >= toDateKey(addDays(now, -6)) && t.label <= todayStr);
  const prior7 = dailyTrend.filter((t) => t.label >= toDateKey(addDays(now, -13)) && t.label < toDateKey(addDays(now, -6)));
  const weekRevenue = last7.reduce((a, t) => a + t.count, 0) * dailyPrice;
  const priorWeekRevenue = prior7.reduce((a, t) => a + t.count, 0) * dailyPrice;

  const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const monthRevenue = (monthTrend.find((t) => t.label === currentMonthKey)?.count || 0) * dailyPrice;

  const twoDayRevenue = twoDayActiveCount * dailyPrice;

  const goalPct = monthlyGoal > 0 ? Math.min(100, Math.round((monthRevenue / monthlyGoal) * 100)) : 0;
  const profileScore = computeProfileScore(gym);

  const todayCapacity = todaySlots.reduce((a, s) => a + s.capacity, 0);
  const todayBooked = todaySlots.reduce((a, s) => a + s.booked, 0);
  const nextSlot = todaySlots.find((s) => s.startMin > nowMin);
  const todayOccupancyPct = todayCapacity > 0 ? Math.round((todayBooked / todayCapacity) * 100) : 0;
  const todaysBookingsCount = recentBookings.filter((b) => b.date === todayStr && b.status !== 'cancelled').length;

  const ratingScore = Math.round(((gym.averageRating || 0) / 5) * 100);
  const healthScore = Math.round((profileScore + ratingScore + todayOccupancyPct) / 3);
  const healthLabel = healthScore >= 80 ? 'Excellent' : healthScore >= 60 ? 'Good' : healthScore >= 40 ? 'Fair' : 'Needs Attention';

  const unreadNotifications = notifications.filter((n) => !n.read).slice(0, 3);

  // Unified activity feed — recent bookings + reviews, both have real dates so they sort together.
  const activity = [
    ...recentBookings.map((b) => ({
      id: `b-${b._id}`,
      type: 'booking',
      date: b.date,
      title: `${b.customerName} — ${b.status === 'cancelled' ? 'cancelled' : b.date < todayStr ? 'completed' : 'upcoming'} booking`,
      sub: `${b.startTime}–${b.endTime}`,
    })),
    ...reviews.map((r) => ({
      id: `r-${r._id}`,
      type: 'review',
      date: r.createdAt?.slice(0, 10),
      title: `${r.reviewerName} left a ${r.rating}-star review`,
      sub: r.comment,
    })),
  ]
    .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
    .slice(0, 6);

  const saveGoal = () => {
    const parsed = parseInt(goalDraft, 10);
    if (Number.isFinite(parsed) && parsed > 0) setMonthlyGoal(parsed);
    setEditingGoal(false);
  };

  return (
    <div className="space-y-6">
      {/* Frequently-changing at-a-glance numbers */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard icon={CalendarCheck} label="Today's Bookings" value={todaysBookingsCount} tone="blue" />
        <StatCard icon={CalendarRange} label="Next 2 Days" value={`₹${twoDayRevenue.toLocaleString('en-IN')}`} sublabel="booked value" tone="purple" />
        <StatCard icon={PiggyBank} label="Revenue (7 days)" value={`₹${weekRevenue.toLocaleString('en-IN')}`} tone="green" trend={trendFor(weekRevenue, priorWeekRevenue)} />
        <StatCard icon={PiggyBank} label="Revenue (This Month)" value={`₹${monthRevenue.toLocaleString('en-IN')}`} tone="amber" />
        <StatCard icon={Percent} label="Today's Occupancy" value={`${todayOccupancyPct}%`} sublabel={`${todayBooked}/${todayCapacity} slots`} tone="blue" />
      </div>

      {/* Today's schedule — the main focus of the page */}
      <div className="google-card p-6 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-base text-[#202124] dark:text-[#e8eaed] flex items-center gap-1.5">
            <Clock className="w-5 h-5 text-[#1a73e8]" /> Today&apos;s Schedule
          </h3>
          <button onClick={() => navigate('/pricing-slots')} className="text-xs text-[#1a73e8] dark:text-[#8ab4f8] hover:underline font-medium flex items-center gap-0.5 cursor-pointer">
            Full Schedule <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
        <ScheduleTimeline
          slots={todaySlots}
          dayCapacity={todayCapacity}
          dayBooked={todayBooked}
          dayOccupancyPct={todayOccupancyPct}
          nowMin={nowMin}
          closed={todayConfig.closed}
          nextSlot={nextSlot}
        />
      </div>

      {/* Booking heatmap, upcoming bookings, notifications */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        <div className="google-card p-5 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
          <h3 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed] mb-3 flex items-center gap-1.5">
            <Flame className="w-4 h-4 text-[#1a73e8]" /> Booking Heatmap
          </h3>
          <div className="grid grid-cols-[58px_1fr_1fr] gap-1.5">
            <div />
            {twoDayHeat.map((d) => (
              <div key={d.label} className="text-center text-[10px] font-semibold text-[#5f6368] dark:text-[#9aa0a6] pb-0.5">
                {d.label}
              </div>
            ))}
            {TIME_BANDS.map((band, i) => (
              <div key={band.label} className="contents">
                <div className="flex items-center text-[10px] text-[#5f6368] dark:text-[#9aa0a6] font-medium">{band.label}</div>
                {twoDayHeat.map((d) => {
                  const cell = d.bands[i];
                  const empty = d.closed || cell.pct === null;
                  return (
                    <div
                      key={d.label}
                      className="rounded-lg h-8 flex items-center justify-center text-[11px] font-bold text-[#202124] dark:text-[#e8eaed] border border-dashed border-[#dadce0] dark:border-[#3c4043]"
                      style={{ backgroundColor: empty ? 'transparent' : occupancyColor(cell.pct), borderStyle: empty ? 'dashed' : 'none' }}
                      title={empty ? 'No slots' : `${cell.booked}/${cell.capacity} booked`}
                    >
                      {empty ? '—' : cell.booked}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#e8eaed] dark:border-[#3c4043]">
            <span className="text-[9px] text-[#9aa0a6]">Quiet</span>
            <div className="flex-1 h-1.5 rounded-full" style={{ background: 'linear-gradient(90deg, #34A853, #FBBC05, #EA4335)' }} />
            <span className="text-[9px] text-[#9aa0a6]">Busy</span>
          </div>
        </div>

        <div className="google-card p-5 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed] flex items-center gap-1.5">
              <CalendarClock className="w-4 h-4 text-[#1a73e8]" /> Upcoming Bookings
            </h3>
            <button onClick={() => navigate('/bookings')} className="text-xs text-[#1a73e8] dark:text-[#8ab4f8] hover:underline font-medium cursor-pointer">
              View All
            </button>
          </div>
          {upcomingBookings.length === 0 ? (
            <p className="text-xs text-[#9aa0a6] py-6 text-center">No upcoming bookings.</p>
          ) : (
            <div className="space-y-2">
              {upcomingBookings.map((b) => (
                <div key={b._id} className="flex items-center justify-between gap-2 text-xs p-2.5 rounded-lg bg-[#f8fafd] dark:bg-[#202124]/50 border border-[#e8eaed] dark:border-[#3c4043]">
                  <div className="min-w-0">
                    <div className="font-semibold text-[#202124] dark:text-[#e8eaed] truncate">{b.customerName}</div>
                    <div className="text-[10px] text-[#9aa0a6]">{b.date} · {b.startTime}–{b.endTime}</div>
                  </div>
                  <span className="font-bold text-[#202124] dark:text-[#e8eaed] shrink-0">₹{dailyPrice}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="google-card p-5 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed] flex items-center gap-1.5">
              <Bell className="w-4 h-4 text-[#1a73e8]" /> Notifications
            </h3>
            {unreadNotifications.length > 0 && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#1a73e8] text-white">{unreadNotifications.length} new</span>
            )}
          </div>
          {unreadNotifications.length === 0 ? (
            <p className="text-xs text-[#9aa0a6] py-6 text-center">You&apos;re all caught up.</p>
          ) : (
            <div className="space-y-2">
              {unreadNotifications.map((n) => (
                <div key={n._id} className="p-2.5 rounded-xl bg-[#e8f0fe] dark:bg-[#1a2e4c] border border-blue-200 dark:border-blue-900">
                  <h4 className="text-xs font-semibold text-[#202124] dark:text-[#e8eaed] truncate">{n.title}</h4>
                  <p className="text-[11px] text-[#5f6368] dark:text-[#9aa0a6] truncate mt-0.5">{n.message}</p>
                </div>
              ))}
            </div>
          )}
          <button
            onClick={() => navigate('/notifications')}
            className="mt-3 text-xs text-[#1a73e8] dark:text-[#8ab4f8] hover:underline font-medium flex items-center gap-0.5 cursor-pointer"
          >
            View All <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Quick actions */}
      <div className="google-card p-5 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
        <h3 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed] mb-4">Quick Actions</h3>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
          {QUICK_ACTIONS.map((a) => (
            <button
              key={a.id}
              onClick={() => navigate(a.path)}
              className="flex flex-col items-center gap-2 p-3 rounded-2xl border border-[#e8eaed] dark:border-[#3c4043] hover:border-[#1a73e8] hover:bg-[#f8fafd] dark:hover:bg-[#1a2e4c]/40 transition-colors cursor-pointer group"
            >
              <div className={`p-2.5 rounded-xl ${TONE_CLASSES[a.tone]} group-hover:scale-105 transition-transform`}>
                <a.icon className="w-4 h-4" />
              </div>
              <span className="text-[11px] font-medium text-[#5f6368] dark:text-[#9aa0a6] text-center leading-tight">{a.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Listing summary card */}
      <div className="google-card bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043] overflow-hidden">
        <div className="h-24 sm:h-28 bg-gradient-to-r from-[#1a73e8] to-[#4285f4] relative flex items-end p-4">
          <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide bg-black/25 text-white backdrop-blur-sm capitalize">{gym.gymType}</span>
        </div>
        <div className="p-5 sm:p-6">
          <span className="text-[11px] font-semibold text-[#5f6368] dark:text-[#9aa0a6] uppercase tracking-wider">Listing Preview</span>
          <h2 className="text-xl font-bold text-[#202124] dark:text-[#e8eaed] mt-0.5">{gym.name}</h2>
          <div className="flex items-center gap-1 text-xs text-[#5f6368] dark:text-[#9aa0a6] mt-1">
            <MapPin className="w-3.5 h-3.5" />
            {gym.city}, {gym.state}
          </div>
          <div className="flex items-center gap-1 mt-2 text-sm">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} className={`w-4 h-4 ${i < Math.round(gym.averageRating || 0) ? 'fill-[#FBBC05] text-[#FBBC05]' : 'text-[#dadce0] dark:text-[#3c4043]'}`} />
            ))}
            <span className="text-[#5f6368] dark:text-[#9aa0a6] ml-1">({gym.totalReviews || 0})</span>
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-3">
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#e6f4ea] text-[#1e8e3e]">₹{dailyPrice}/day</span>
            {(gym.amenities || []).slice(0, 4).map((a) => (
              <span key={a} className="px-2.5 py-1 rounded-full text-xs font-medium bg-[#f1f3f4] dark:bg-[#202124] text-[#5f6368] dark:text-[#9aa0a6] capitalize">
                {a}
              </span>
            ))}
          </div>

          <button
            onClick={() => navigate('/listing-preview')}
            className="mt-4 text-sm font-semibold text-[#1a73e8] dark:text-[#8ab4f8] hover:underline flex items-center gap-1 cursor-pointer"
          >
            See full listing preview <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Activity feed */}
      <div className="google-card p-6 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
        <div className="flex items-center justify-between pb-4 border-b border-[#e8eaed] dark:border-[#3c4043]">
          <h3 className="font-semibold text-base text-[#202124] dark:text-[#e8eaed] flex items-center gap-2">
            <Activity className="w-5 h-5 text-[#1a73e8]" /> Recent Activity
          </h3>
          <button onClick={() => navigate('/bookings')} className="text-xs text-[#1a73e8] dark:text-[#8ab4f8] hover:underline font-medium flex items-center gap-0.5 cursor-pointer">
            View All <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
        {activity.length === 0 ? (
          <p className="text-xs text-[#9aa0a6] py-8 text-center">No recent activity yet.</p>
        ) : (
          <div className="space-y-3 mt-4">
            {activity.map((a) => (
              <div key={a.id} className="p-3.5 rounded-xl bg-[#f8fafd] dark:bg-[#202124]/50 border border-[#e8eaed] dark:border-[#3c4043] flex items-center gap-3">
                <div className={`p-2 rounded-lg shrink-0 ${a.type === 'booking' ? 'bg-blue-50 dark:bg-blue-950/40 text-[#1a73e8]' : 'bg-amber-50 dark:bg-amber-950/40 text-[#f29900]'}`}>
                  {a.type === 'booking' ? <CalendarCheck className="w-4 h-4" /> : <Star className="w-4 h-4" />}
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed] truncate">{a.title}</h4>
                  <span className="text-xs text-[#5f6368] dark:text-[#9aa0a6] truncate block">{a.sub}</span>
                </div>
                <span className="text-[11px] text-[#9aa0a6] shrink-0">{a.date}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* De-emphasized: things that rarely change day-to-day */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="google-card p-4 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
          <div className="flex items-center gap-1.5">
            <Star className="w-4 h-4 fill-[#FBBC05] text-[#FBBC05]" />
            <span className="text-lg font-bold text-[#202124] dark:text-[#e8eaed]">{(gym.averageRating || 0).toFixed(1)}</span>
          </div>
          <span className="text-[10px] text-[#9aa0a6]">Rating · {gym.totalReviews || 0} reviews</span>
        </div>

        <div className="google-card p-4 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
          <div className="flex items-center justify-between">
            <span className="text-xs text-[#5f6368] dark:text-[#9aa0a6] flex items-center gap-1">
              <TrendingUp className="w-3.5 h-3.5" /> Profile
            </span>
            <span className="text-sm font-bold text-[#202124] dark:text-[#e8eaed]">{profileScore}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-[#f1f3f4] dark:bg-[#202124] mt-2 overflow-hidden">
            <div className="h-full rounded-full bg-[#9334e6]" style={{ width: `${profileScore}%` }} />
          </div>
        </div>

        <div className="google-card p-4 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043] flex items-center gap-3">
          <GaugeChart pct={healthScore / 100} color={occupancySolid(1 - healthScore / 100)} size={44} strokeWidth={6} centerLabel="" />
          <div className="min-w-0">
            <div className="flex items-center gap-1 text-xs text-[#5f6368] dark:text-[#9aa0a6]">
              <HeartPulse className="w-3.5 h-3.5" /> Health
            </div>
            <div className="text-sm font-bold text-[#202124] dark:text-[#e8eaed]">
              {healthScore} · {healthLabel}
            </div>
          </div>
        </div>

        <div className="google-card p-4 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-[#5f6368] dark:text-[#9aa0a6] flex items-center gap-1">
              <Target className="w-3.5 h-3.5" /> Monthly Goal
            </span>
            {!editingGoal && (
              <button
                onClick={() => {
                  setGoalDraft(String(monthlyGoal));
                  setEditingGoal(true);
                }}
                className="text-[#9aa0a6] hover:text-[#1a73e8] cursor-pointer"
                aria-label="Edit goal"
              >
                <PencilLine className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {editingGoal ? (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-[#5f6368] dark:text-[#9aa0a6]">₹</span>
              <input
                autoFocus
                type="number"
                min="1"
                value={goalDraft}
                onChange={(e) => setGoalDraft(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && saveGoal()}
                className="google-input-box flex-1 !py-1 !text-xs"
              />
              <button onClick={saveGoal} className="p-1 rounded-md bg-[#1a73e8] text-white cursor-pointer">
                <Check className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <>
              <div className="text-sm font-bold text-[#202124] dark:text-[#e8eaed]">
                {goalPct}% <span className="text-[10px] font-normal text-[#9aa0a6]">of ₹{monthlyGoal.toLocaleString('en-IN')}</span>
              </div>
              <div className="h-1.5 rounded-full bg-[#f1f3f4] dark:bg-[#202124] mt-1.5 overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-[#1a73e8] to-[#34A853]" style={{ width: `${goalPct}%` }} />
              </div>
            </>
          )}
        </div>
      </div>

      {profileScore < 100 && (
        <div className="p-4 rounded-2xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 flex items-start gap-3 text-xs text-amber-900 dark:text-amber-200">
          <Sparkles className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
          <span>Your listing is {profileScore}% complete. Head to My Gym to fill in the rest — verified, complete profiles get more visibility.</span>
        </div>
      )}
    </div>
  );
}
